import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { cachedFetch, clearCache } from '../utils/apiCache';
import authFetch from '../utils/authFetch';
import { API_BASE } from '../utils/apiBase';
const EVENTS_TTL = 5 * 60 * 1000; // 5 minutes

const EventsContext = createContext(null);

export const useEvents = () => {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error('useEvents must be used within an EventsProvider');
  }
  return context;
};

export const EventsProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [categories, setCategories] = useState(['tous']);
  const [selectedCategory, setSelectedCategory] = useState('tous');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  const hasFetched = useRef(false);

  // Fetch events from PCC website (auto-synced)
  const fetchEvents = useCallback(async (category = null, forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE}/api/events-pcc?upcoming_only=true&limit=50`;
      const data = await cachedFetch(url, { ttl: EVENTS_TTL, forceRefresh });
      let eventsList = data.events || [];

      // IMPORTANT: never display past events — safety filter at display level (backup for scraper)
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      eventsList = eventsList.filter(e => {
        const eventDate = new Date(e.startDate || e.date);
        return !isNaN(eventDate.getTime()) && eventDate >= todayMidnight;
      });

      // IMPORTANT: title safety-net — fix encoding/spacing at display level (backup for scraper)
      const fixTitleSafety = (t) => {
        if (!t) return t;
        // Decode common HTML entities
        let s = t.replace(/&amp;/g, '&').replace(/&eacute;/g, 'é').replace(/&egrave;/g, 'è')
                 .replace(/&agrave;/g, 'à').replace(/&ecirc;/g, 'ê').replace(/&ocirc;/g, 'ô')
                 .replace(/&ucirc;/g, 'û').replace(/&iuml;/g, 'ï').replace(/&ccedil;/g, 'ç')
                 .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#039;/g, "'");
        // Fix camelCase
        s = s.replace(/([a-zà-ÿ])([A-ZÀ-Ÿ])/g, '$1 $2');
        // Fix uppercase blocks
        s = s.replace(/([A-ZÀ-Ÿ]{2,})([A-ZÀ-Ÿ][a-zà-ÿ])/g, '$1 $2');
        // Clean whitespace
        s = s.replace(/\s+/g, ' ').trim();
        // Capitalize first letter
        if (s) s = s[0].toUpperCase() + s.slice(1);
        return s;
      };
      eventsList = eventsList.map(e => ({
        ...e,
        title: fixTitleSafety(e.title)
      }));

      // Extract unique categories from events
      const uniqueCategories = ['tous', ...new Set(
        eventsList
          .map(e => e.category || detectCategory(e.title))
          .filter(Boolean)
      )];
      setCategories(uniqueCategories);
      
      // Add category to events that don't have one
      eventsList = eventsList.map(e => ({
        ...e,
        category: e.category || detectCategory(e.title)
      }));
      
      // Filter by category if specified
      if (category && category !== 'tous') {
        eventsList = eventsList.filter(e => 
          e.category?.toLowerCase() === category.toLowerCase()
        );
      }
      
      setEvents(eventsList);
      
      // Mark first 2 events as featured
      const featured = eventsList.slice(0, 2).map(e => ({ ...e, isFeatured: true }));
      setFeaturedEvents(featured);
      
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
      
      // Fallback to legacy events API if new one fails
      try {
        const fallbackData = await authFetch('/api/events-public');
        setEvents(fallbackData.events || []);
        setCategories(fallbackData.categories || ['tous']);
        const featured = (fallbackData.events || []).filter(e => e.isFeatured);
        setFeaturedEvents(featured);
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Detect category from event title
  const detectCategory = (title) => {
    if (!title) return 'église';
    const t = title.toLowerCase();
    
    if (t.includes('culte') || t.includes('dimanche')) return 'culte';
    if (t.includes('jeune') || t.includes('youth')) return 'jeunesse';
    if (t.includes('séminaire') || t.includes('seminaire')) return 'séminaire';
    if (t.includes('aîné') || t.includes('aine') || t.includes('chandeleur')) return 'aînés';
    if (t.includes('conférence') || t.includes('conference')) return 'conférence';
    if (t.includes('prière') || t.includes('priere')) return 'prière';
    if (t.includes('famille') || t.includes('couple')) return 'famille';
    if (t.includes('enfant') || t.includes('kids')) return 'enfants';
    if (t.includes('mission')) return 'mission';
    if (t.includes('rencontre') || t.includes('dieu')) return 'prière';
    
    return 'église';
  };

  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const status = await authFetch('/api/events-pcc/sync-status');
      setSyncStatus(status);
    } catch (err) {
      console.error('Error fetching sync status:', err);
    }
  }, []);

  // Fetch single event
  const fetchEventById = useCallback(async (eventId) => {
    const fixSingleTitle = (t) => {
      if (!t) return t;
      let s = t.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#039;/g, "'").replace(/&eacute;/g, 'é').replace(/&egrave;/g, 'è').replace(/&agrave;/g, 'à').replace(/&ecirc;/g, 'ê').replace(/&ocirc;/g, 'ô').replace(/&ucirc;/g, 'û').replace(/&iuml;/g, 'ï').replace(/&ccedil;/g, 'ç');
      s = s.replace(/([a-z\u00E0-\u00F6\u00F8-\u00FF])([A-Z\u00C0-\u00D6\u00D8-\u00DE])/g, '$1 $2');
      s = s.replace(/([a-zA-Z])\s([éèêëàâîïôûùüç])(?=[a-zA-Zéèêëàâîïôûùüç])/g, '$1$2');
      return s.trim();
    };
    try {
      let data;
      try {
        data = await authFetch(`/api/events-pcc/${eventId}`);
      } catch {
        data = await authFetch(`/api/events-public/${eventId}`);
      }
      if (!data) return null;
      // Normalize field names — scraper returns {date, image}, other APIs use {startDate, imageUrl}
      if (data.title) data.title = fixSingleTitle(data.title);
      data.startDate = data.startDate || data.date;
      data.imageUrl = data.imageUrl || data.image;
      data.category = data.category || detectCategory(data.title);
      return data;
    } catch (err) {
      console.error('Error fetching event:', err);
      return null;
    }
  }, []);

  // Filter by category
  const filterByCategory = useCallback((category) => {
    setSelectedCategory(category);
    fetchEvents(category);
  }, [fetchEvents]);

  // Format date for display (French locale)
  const formatEventDate = useCallback((dateString, includeTime = true) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      
      if (includeTime) {
        return date.toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }, []);

  // Format short date
  const formatShortDate = useCallback((dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      });
    } catch {
      return '';
    }
  }, []);

  // Format time only
  const formatTime = useCallback((dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  }, []);

  // Get upcoming events (next 3)
  const getUpcomingEvents = useCallback(() => {
    return events.slice(0, 3);
  }, [events]);

  // Get category display name
  const getCategoryDisplayName = useCallback((category) => {
    // Null-safe: fall back to "Événement" if category is missing/undefined
    if (!category || typeof category !== 'string') return 'Événement';
    const names = {
      'tous': 'Tous',
      'culte': 'Culte',
      'jeunesse': 'Jeunesse',
      'séminaire': 'Séminaire',
      'aînés': 'Aînés',
      'conférence': 'Conférence',
      'prière': 'Prière',
      'famille': 'Famille',
      'enfants': 'Enfants',
      'mission': 'Mission',
      'église': 'Église'
    };
    return names[category] || category.charAt(0).toUpperCase() + category.slice(1);
  }, []);

  // Get category color
  const getCategoryColor = useCallback((category) => {
    const colors = {
      'culte': 'bg-amber-100 text-amber-800',
      'jeunesse': 'bg-purple-100 text-purple-800',
      'séminaire': 'bg-blue-100 text-blue-800',
      'aînés': 'bg-green-100 text-green-800',
      'conférence': 'bg-red-100 text-red-800',
      'prière': 'bg-indigo-100 text-indigo-800',
      'famille': 'bg-pink-100 text-pink-800',
      'enfants': 'bg-orange-100 text-orange-800',
      'mission': 'bg-teal-100 text-teal-800',
      'église': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  }, []);

  // Initial fetch (only once)
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchEvents();
      fetchSyncStatus();
    }
  }, [fetchEvents, fetchSyncStatus]);

  const value = useMemo(() => ({
    // State
    events,
    featuredEvents,
    categories,
    selectedCategory,
    loading,
    error,
    syncStatus,

    // Functions
    fetchEvents,
    fetchEventById,
    filterByCategory,
    formatEventDate,
    formatShortDate,
    formatTime,
    getUpcomingEvents,
    getCategoryDisplayName,
    getCategoryColor,
    fetchSyncStatus
  }), [
    events, featuredEvents, categories, selectedCategory, loading, error, syncStatus,
    fetchEvents, fetchEventById, filterByCategory, formatEventDate, formatShortDate,
    formatTime, getUpcomingEvents, getCategoryDisplayName, getCategoryColor, fetchSyncStatus
  ]);

  return (
    <EventsContext.Provider value={value}>
      {children}
    </EventsContext.Provider>
  );
};

export default EventsContext;
